import * as React from 'react';
import { useThemeUI } from '@theme-ui/core';
import { Box, BoxProps } from '@theme-ui/components';
import { getSpace } from './_commons/spacing';

type InheritedProps = Omit<BoxProps, 'ref'>;

export interface ColumnsProps extends InheritedProps {
  gap?: number | string;
  colGap?: number;
  rowGap?: number;
  columns?: number[][];
  rows?: string[];
}

export const Columns = React.forwardRef<HTMLDivElement, ColumnsProps>(
  ({ gap = 0, colGap, rowGap, columns = [[1]], ...props }, ref) => {
    const { theme } = useThemeUI();
    const itemCount = React.Children.count(props.children);
    const gridColumnGap = colGap ?? gap;
    const gridRowGap = rowGap ?? gap;

    let rows: number | number[];
    let gridTemplateColumns;

    let msGridColumns;
    let msGridRows;

    // Get the number of rows necessary to place all items.
    // We cannot rely on implicit grid behavior, so we can use it in IE 10-11.
    const computeRows = (col: number[]) => Math.ceil(itemCount / col.length);
    const computeRowPosition = (index: number, cols: number) => 2 * Math.ceil(index / cols) - 1;
    const computeColumnPosition = (index: number, cols: number) =>
      2 * (index % cols === 0 ? cols : index % cols) - 1;

    const columnsTemplate = (n: number[]) => n.map((i) => `${i}fr`).join(' ');

    // There is no gap property in Internet Explorer.
    // We have to fake it with additional columns
    const msColumnsTemplate = (n: number[]) => {
      return n.map((i) => `${i}fr`).join(` ${getSpace(theme, gridColumnGap)} `);
    };
    const msRowsTemplate = (n: number) => {
      if (props.rows) {
        let aux: string = '';
        props.rows.forEach((element, index) => {
          aux = aux + ` ${getSpace(theme, element)} `;
        });
        return aux;
      } else {
        return Array(n)
          .fill('auto')
          .join(` ${getSpace(theme, gridRowGap)} `);
      }
    };

    const childrenPositions: any = {};

    rows = columns.map(computeRows);
    gridTemplateColumns = columns.map(columnsTemplate);
    msGridColumns = columns.map(msColumnsTemplate);
    msGridRows = rows.map(msRowsTemplate);
    let gridTemplateRows = props.rows ? msGridRows : undefined;
    for (let i = 1; i <= itemCount; i++) {
      childrenPositions[`> div:nth-of-type(${i})`] = {
        msGridRow: columns.map((cols) => computeRowPosition(i, cols.length)),
        msGridColumn: columns.map((cols) => computeColumnPosition(i, cols.length))
      };
    }

    return (
      <Box
        ref={ref}
        {...props}
        // @ts-ignore
        __themeKey="grids"
        __css={{
          '@supports (display: grid)': {
            display: 'grid',
            gridColumnGap,
            gridRowGap,
            gridTemplateColumns,
            gridTemplateRows
          },
          display: '-ms-grid',
          msGridColumns,
          msGridRows,
          ...childrenPositions
        }}
      />
    );
  }
);
